import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ModularWlWysiwygEditorComponent } from 'src/app/shared/widgets/modular-wysiwyg-editor/modular-wysiwyg-editor.component';
import { ActivatedRoute } from '@angular/router';
import { SelectItem } from 'primeng/api';
import { EventReminder } from 'src/app/events-v4/shared/notifications/event-reminder.model';
import { NotificationService, UtilService } from 'src/app/shared';
import { CommunicationService } from 'src/app/communication/shared/communication.service';
import { EventV4ReminderService } from 'src/app/events-v4/shared/notifications/event-v4-reminder.service';
import { TimeZone } from '../../../../../config/shared/time-zone/time-zone.model';

@Component({
  selector: 'wl-event-v4-settings-notification-reminder-edit-dialog',
  templateUrl: './event-v4-settings-reminder-edit-dialog.component.html',
  styleUrls: ['./event-v4-settings-reminder-edit-dialog.component.less'],
  standalone: false,
})
export class EventV4SettingsNotificationReminderEditDialogComponent {
  @Input() eventTimeZone: TimeZone;
  @Input() eventDate: Date;
  @Output() reminderSaved = new EventEmitter<boolean>();
  @ViewChild(ModularWlWysiwygEditorComponent) private _emailBodyEditor: ModularWlWysiwygEditorComponent;

  eventId: number;
  reminder: EventReminder;

  specialFields: Array<SelectItem> = [];
  showCreateEditModal: boolean = false;
  isEditing: boolean;
  isSaving: boolean = false;
  showEditor: boolean = false;
  reminderPast: boolean = false;
  reminderDateTimeFormatted: string = '';

  constructor(
    private _route: ActivatedRoute,
    private _utilService: UtilService,
    private _reminderService: EventV4ReminderService,
    private _notificationService: NotificationService,
    private _communicationService: CommunicationService
  ) {}

  ngOnInit() {
    this._route.params.subscribe((params) => {
      if (this._utilService.isNumeric(params['id'])) {
        const id = Number(params['id']);
        this.eventId = id;
      }
    });

    this._communicationService.getEventConfirmationEmailSpecialFields().then((specialFields) => {
      if (specialFields) {
        this.specialFields = specialFields.map<SelectItem>((field) => {
          return { label: field.name, value: field.value };
        });
      }
    });
  }

  public openDialog(eventReminder: EventReminder): void {
    this.reminder = eventReminder;
    this.isEditing = (eventReminder?.DraftMessageId ?? 0) > 0 || (eventReminder?.MessageId ?? 0) > 0;
    this.showCreateEditModal = true;
    this.showEditor = true;
    this.recalculatePast();
  }

  public closeDialog(): void {
    this.isSaving = false;
    this.showCreateEditModal = false;
    this.showEditor = false;
  }

  public async save(): Promise<void> {
    if (this.isSaving) return;
    this.isSaving = true;
    // save the editor's inlined-style HTML, not the raw editor data, so the reminder renders in email clients
    await this._emailBodyEditor?.flushInlineStyles();

    this.reminder.EventId = this.eventId;

    const crudPromise = this.isEditing
      ? this._reminderService.updateReminder(this.reminder)
      : this._reminderService.createReminder(this.reminder);

    crudPromise
      .then(() => {
        this._notificationService.translateSuccess('common.saved-successfully');
        this.reminderSaved.emit(true);
        this.closeDialog();
      })
      .catch((error) => {
        this._notificationService.httpError(error);
      })
      .finally(() => {
        this.isSaving = false;
      });
  }

  onDaysBeforeEventChange(value: number) {
    this.reminder.DaysBeforeEvent = Number(value) || 0;
    this.recalculatePast();
  }

  onSendTimeChange(value: any) {
    this.reminder.SendTime = this.formatReminderDateTime(value) || '';
    this.recalculatePast();
  }

  private recalculatePast(): void {
    try {
      if (!this.eventDate || !this.reminder || this.reminder.DaysBeforeEvent === undefined || this.reminder.DaysBeforeEvent === null) {
        this.reminderPast = false;
        this.reminderDateTimeFormatted = '';
        return;
      }

      // Calculate the actual reminder send date/time
      const reminderDateTime = this.calculateReminderDateTime();
      if (!reminderDateTime) {
        this.reminderPast = false;
        this.reminderDateTimeFormatted = '';
        return;
      }

      // Format the reminder datetime for display
      this.reminderDateTimeFormatted = this.formatReminderDateTime(reminderDateTime);

      // Get current time in event timezone
      const eventOffsetMinutes = this.getEventUtcOffsetMinutes();
      const now = new Date();
      const nowUtcMs = now.getTime() + now.getTimezoneOffset() * 60 * 1000;
      const nowEventMs = nowUtcMs + eventOffsetMinutes * 60 * 1000;

      // Compare: if reminder datetime is before now in event timezone, it's in the past
      this.reminderPast = reminderDateTime.getTime() < nowEventMs;
    } catch {
      this.reminderPast = false;
      this.reminderDateTimeFormatted = '';
    }
  }

  private calculateReminderDateTime(): Date | null {
    if (!this.eventDate || !this.reminder?.SendTime) {
      return null;
    }

    const parsedTime = this.parseSendTime(this.reminder.SendTime);
    if (!parsedTime) {
      return null;
    }

    // Calculate reminder date: event date minus DaysBeforeEvent
    const eventDateCopy = new Date(this.eventDate);
    const msPerDay = 24 * 60 * 60 * 1000;
    const reminderDateMs = eventDateCopy.getTime() - this.reminder.DaysBeforeEvent * msPerDay;
    const reminderDate = new Date(reminderDateMs);
    reminderDate.setHours(parsedTime.hours, parsedTime.minutes, 0, 0);

    return reminderDate;
  }

  private formatReminderDateTime(date: Date): string {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;

    return `${month}/${day}/${year} ${hours12}:${minutes} ${ampm}`;
  }

  private parseSendTime(timeValue: string): { hours: number; minutes: number } | null {
    if (!timeValue) {
      return null;
    }

    const match = /(\d{1,2}):(\d{2})\s*(AM|PM)/i.exec(timeValue.trim());
    if (!match) {
      return null;
    }

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();

    if (period === 'PM' && hours !== 12) {
      hours += 12;
    }

    if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return { hours, minutes };
  }

  private getEventUtcOffsetMinutes(): number {
    const fallback = -new Date().getTimezoneOffset();
    if (!this.eventTimeZone || this.eventTimeZone.UtcOffSet === undefined || this.eventTimeZone.UtcOffSet === null) {
      return fallback;
    }

    const offset = this.eventTimeZone.UtcOffSet;
    // Some APIs return hours, others minutes. Assume hours if magnitude looks like an hour value.
    if (Math.abs(offset) <= 14) {
      return offset * 60;
    }
    return offset;
  }
}
